import React, { useEffect, useState, useRef } from "react";
import { pendingApprovalActivities, approveActivity } from "../api/adminService"; // Import API functions

const PendingActivitiesContent = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(""); // Success message state
  const [selectedActivity, setSelectedActivity] = useState(null); // For modal
  const [approving, setApproving] = useState(false); // For approve button loading state
  const isMounted = useRef(false);

  const fetchActivities = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await pendingApprovalActivities();
      setActivities(response.pendingActivities || []);
    } catch (err) {
      setError("Failed to fetch pending activities. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isMounted.current) {
      fetchActivities();
      isMounted.current = true;
    }
  }, []);

  const handleViewDetails = (activity) => {
    setSelectedActivity(activity); // Set activity for modal
  };

  const handleCloseModal = () => {
    setSelectedActivity(null); // Clear modal state
  };

  const handleApprove = async () => {
    if (!selectedActivity) return;

    setApproving(true);
    try {
      await approveActivity(selectedActivity.activityId);
      // Remove approved activity from the list
      setActivities((prevActivities) =>
        prevActivities.filter((activity) => activity.activityId !== selectedActivity.activityId)
      );
      setSuccessMessage("Activity approved successfully.");
      setTimeout(() => setSuccessMessage(""), 5000); // Clear success message after 5 seconds
      handleCloseModal(); // Close modal
    } catch (err) {
      setError("Failed to approve activity. Please try again.");
      setTimeout(() => setError(""), 5000); // Clear error message after 5 seconds
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="activities-content">
      <h2 className="text-2xl font-bold mb-4">Pending Activities for Approval</h2>
      <p>Below is the list of activities awaiting approval:</p>

      {/* Success Message */}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}

      {/* Error Message */}
      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      ) : activities.length > 0 ? (
        <table className="table table-striped">
          <thead>
            <tr>
              <th>#</th>
              <th>Title</th>
              <th>Description</th>
              <th>Type</th>
              <th>Location</th>
              <th>Venue</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {activities.map((activity, index) => (
              <tr key={activity.activityId}>
                <td>{index + 1}</td>
                <td>{activity.title}</td>
                <td>{activity.description}</td>
                <td>{activity.type}</td>
                <td>{activity.location}</td>
                <td>{activity.venueName || "N/A"}</td> {/* Add venueName here */}
                <td>
                  <button
                    className="btn btn-info btn-sm"
                    onClick={() => handleViewDetails(activity)}
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>No pending activities found.</p>
      )}

      {/* Modal for Viewing Details */}
      {selectedActivity && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Activity Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  <strong>Title:</strong> {selectedActivity.title}
                </p>
                <p>
                  <strong>Description:</strong> {selectedActivity.description}
                </p>
                <p>
                  <strong>Type:</strong> {selectedActivity.type}
                </p>
                <p>
                  <strong>Location:</strong> {selectedActivity.location}
                </p>
                <p>
                  <strong>Venue:</strong> {selectedActivity.venueName || "N/A"}
                </p> {/* Add venueName in modal */}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  Close
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleApprove}
                  disabled={approving}
                >
                  {approving ? "Approving..." : "Approve"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingActivitiesContent;
